/**
 * QuizGenerator — Quiz à choix multiples (Carbon Design System)
 */

import { useState } from 'react';
import { MachineLearning } from '@carbon/icons-react';
import {
  FileUpload,
  LanguageSelector,
  AIProcessingCard,
  QuizDisplay,
  AiGeneratorPageLayout,
  AiGeneratorFormCard,
  AiFormStep,
  AiGenerateButton,
} from '@sphere/learning/ui';
import { useGenerateQuiz, useFileValidation } from '@sphere/learning/data-access';
import type { SupportedLanguage, QuizSchema } from '@sphere/ai/domain';
import { handleAIError } from '@sphere/ai/data-access';
import { downloadBlob } from '@sphere/shared/util';
import tw from './QuizGenerator.tailwind.module.scss';

export default function QuizGenerator() {
  const [language, setLanguage] = useState<SupportedLanguage>('fr' as SupportedLanguage);
  const [generatedQuiz, setGeneratedQuiz] = useState<QuizSchema | null>(null);

  const { file, validation, setFile } = useFileValidation();
  const generateQuiz = useGenerateQuiz();

  const handleGenerate = async () => {
    if (!file || !validation.isValid) return;
    try {
      const quiz = await generateQuiz.mutateAsync({ file, lang: language });
      setGeneratedQuiz(quiz);
    } catch (error) {
      console.error('Quiz generation failed:', error);
    }
  };

  const handleNewQuiz = () => {
    setGeneratedQuiz(null);
    setFile(null);
  };

  const handleDownloadQuiz = () => {
    if (!generatedQuiz) return;
    const content =
      `${generatedQuiz.title || 'AI Quiz'}\n\n` +
      generatedQuiz.questions
        .map(
          (q, i) =>
            `Question ${i + 1}: ${q.question}\n` +
            q.choices
              .map((opt, j) => `${String.fromCharCode(65 + j)}) ${opt}`)
              .join('\n') +
            `\nAnswer: ${q.answer}\n\n---\n`
        )
        .join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    downloadBlob(blob, `quiz-${Date.now()}.txt`);
  };

  if (generatedQuiz) {
    return (
      <div className={tw.u1}>
        <div style={{ maxWidth: '90rem', margin: '0 auto', padding: '0 1.5rem' }}>
          <QuizDisplay
            quiz={generatedQuiz}
            onDownload={handleDownloadQuiz}
            onRestart={handleNewQuiz}
          />
        </div>
      </div>
    );
  }

  return (
    <AiGeneratorPageLayout
        icon={MachineLearning}
        title="Générateur de Quiz IA"
        description="Transformez vos documents en quiz interactifs à choix multiples. Une solution professionnelle pour tester et améliorer la rétention des connaissances."
      >
        <AiGeneratorFormCard title="Créer un Quiz" icon={MachineLearning}>
          <AiFormStep step={1} label="Sélectionnez votre document">
            <FileUpload
              file={file}
              onFileChange={setFile}
              disabled={generateQuiz.isPending}
            />
            {validation.error && (
              <p className={`cds--form-requirement ${tw.u2}`}>{validation.error}</p>
            )}
          </AiFormStep>
          <AiFormStep step={2} label="Choisissez la langue">
            <LanguageSelector
              value={language}
              onChange={setLanguage}
              disabled={generateQuiz.isPending}
            />
          </AiFormStep>
          <AiFormStep step={3} label="Générez votre quiz">
            <AiGenerateButton
              onClick={handleGenerate}
              disabled={!file || !validation.isValid || generateQuiz.isPending}
              isLoading={generateQuiz.isPending}
              label="Générer le Quiz"
              loadingLabel="Génération..."
              icon={MachineLearning}
            />
          </AiFormStep>
        </AiGeneratorFormCard>

        {generateQuiz.isPending && (
          <div className={tw.u3}>
            <AIProcessingCard
              isLoading
              error={null}
              title="Génération du quiz..."
              description="L'IA analyse votre document et crée des questions pertinentes..."
            />
          </div>
        )}

        {generateQuiz.error && (
          <div className={tw.u3}>
            <AIProcessingCard
              isLoading={false}
              error={handleAIError(generateQuiz.error)}
              title="Erreur de génération"
              onRetry={handleGenerate}
            />
          </div>
        )}
      </AiGeneratorPageLayout>
  );
}
